import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box } from "@mui/material"
import { useAppSelector, useAppDispatch } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import { wasmModule } from "@/gluelayer"
import { IPrintSettingJsonObj } from "@/core/app/slices/user/user.type"

export const StageNumber: React.FC<{
  jsonData?: IPrintSettingJsonObj
  updateJsonData: (data: IPrintSettingJsonObj) => void
}> = ({ jsonData, updateJsonData }) => {
  // checkbox if No stage
  const [checkNoStage, setCheckNoStage] = useState<string>("")
  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateJsonData({
      LabelStagingNumber:
        (event.target as HTMLInputElement).value === "1"
          ? "StageNo"
          : "StageYes",
    })
  }
  // tooth number
  const [toothNumber, setToothNumber] = useState<string>("")
  const handleToothNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value
    updateJsonData({ SelectToothNumber: value })
  }

  // side number
  const [sideNumber, setSideNumber] = useState<string>("")
  const handleSideNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value
    updateJsonData({ SelectSideNumber: value })
  }
  // stage number size ,default 1 ---big   0---small
  const [snSize, setSnSize] = useState<number>(1)
  const handleStageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSnSize(+event.target.value)
  }
  // init tabs value
  useEffect(() => {
    if (!jsonData|| Object.keys(jsonData).length === 0) {
      return
    }

    const LabelStagingNumber = jsonData.LabelStagingNumber
    const LabelStagingNumberStart = jsonData.SelectToothNumber
    const StageNumberLeftOrRight = jsonData.SelectSideNumber

    if (LabelStagingNumber === "StageNo") {
      setCheckNoStage("1")
    } else {
      setCheckNoStage("0")
    }

    if (LabelStagingNumberStart !== "" && StageNumberLeftOrRight !== "") {
      setToothNumber(`${LabelStagingNumberStart}`)
      setSideNumber(`${StageNumberLeftOrRight}`)
    }
  }, [jsonData])
  // send value to wasm
  useEffect(() => {
    try {
      const printMoudle = wasmModule.moduleManager.GetModulePrintSelection()
      if (+checkNoStage) {
        if (printMoudle && printMoudle.SetTagPosition) {
          printMoudle.SetTagPosition("StageNo")
        }
      } else {
        if (printMoudle && printMoudle.SetTagPosition) {
          printMoudle.SetTagPosition(`${toothNumber}+${sideNumber}`)
        }
      }
    } catch (error) {
      console.error(error)
    }
  }, [toothNumber, sideNumber, checkNoStage])
  //pass stageNumberSize to wasm
  useEffect(() => {
    try {
      const printMoudle = wasmModule.moduleManager.GetModulePrintSelection()
      if (printMoudle) {
        printMoudle.SetStagingNumberSize(snSize)
      }
    } catch (error) {
      console.log(error)
    }
  }, [snSize, wasmModule.moduleManager.GetModulePrintSelection()])
  return (
    <Box sx={{ width: "100%" }}>
      {/* <FormControlLabel
        control={<Checkbox checked={checkNoStage} onChange={handleCheck} />}
        label="No Stage Number on the model"
        sx={{ margin: "0 0 10px 0" }}
      /> */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          gap: "10px",
        }}
      >
        <Box sx={{ marginRight: "10px", flex: 1 }}>
          <FormControl sx={{ marginBottom: "24px" }}>
            <FormLabel id="stageNumber">{`Include stage number on model`}</FormLabel>
            <RadioGroup
              row
              sx={{ marginLeft: "10px", gap: "40px" }}
              aria-labelledby="stageNumber"
              name="radio-buttons-group"
              value={checkNoStage}
              onChange={handleCheck}
            >
              <FormControlLabel value={"0"} control={<Radio />} label="Yes" />
              <FormControlLabel value={"1"} control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FormControl
            sx={{ marginBottom: "24px" }}
            disabled={Number.parseInt(checkNoStage) > 0}
          >
            <FormLabel id="stageNumberSize">{`Select the font size of stage number`}</FormLabel>
            <RadioGroup
              row
              sx={{ marginLeft: "10px", gap: "40px" }}
              aria-labelledby="stageNumberSize"
              name="radio-buttons-group"
              value={snSize}
              onChange={handleStageSize}
            >
              <FormControlLabel value={0} control={<Radio />} label="Small" />
              <FormControlLabel value={1} control={<Radio />} label="Large" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          gap: "10px",
        }}
      >
        <Box sx={{ marginRight: "10px", flex: 1 }}>
          <FormControl disabled={Number.parseInt(checkNoStage) > 0}>
            <FormLabel id="toothNumber">{`Select the tooth to place stage number`}</FormLabel>
            <RadioGroup
              sx={{ marginLeft: "10px" }}
              aria-labelledby="toothNumber"
              name="radio-buttons-group"
              value={toothNumber}
              onChange={handleToothNumber}
            >
              <FormControlLabel
                value="Second_Premolar-left"
                control={<Radio />}
                label="Left 2nd premolar"
              />
              <FormControlLabel
                value="First_Molar-left"
                control={<Radio />}
                label="Left 1st molar"
              />
              <FormControlLabel
                value="Second_Premolar-right"
                control={<Radio />}
                label="Right 2nd premolar"
              />
              <FormControlLabel
                value="First_Molar-right"
                control={<Radio />}
                label="Right 1st molar"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FormControl disabled={Number.parseInt(checkNoStage) > 0}>
            <FormLabel id="sideNumber">{`Select the side to place stage number`}</FormLabel>
            <RadioGroup
              sx={{ marginLeft: "10px" }}
              aria-labelledby="sideNumber"
              name="radio-buttons-group"
              value={sideNumber}
              onChange={handleSideNumber}
            >
              <FormControlLabel
                value="Buccal"
                control={<Radio />}
                label="Buccal side of the tooth"
              />
              <FormControlLabel
                value="Lingual"
                control={<Radio />}
                label="Lingual side of the tooth"
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>
    </Box>
  )
}
